import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { settingsApi } from '@/shared/api/services'
import type { SystemSettings as ApiSystemSettings } from '@/shared/api/types'
import type { CompanyStyle } from '@/types/auth.types'

export interface GeneralSettings {
  language: string
  timezone: string
}

export interface AISettings {
  model: string
  temperature: number
  maxTokens: number
  enableContextWindow: boolean
  contextWindow: number
  maxResponseChars: number
  enableStreaming: boolean
  apiKey?: string  // 总是空字符串
  hasApiKey?: boolean  // 新增：指示是否有设置的API密钥
  embeddingModel?: string  // "local" | "dashscope"
  embeddingApiKey?: string  // 仅dashscope模型需要，总是空字符串
  hasEmbeddingApiKey?: boolean  // 新增：指示是否有设置的嵌入API密钥
  embeddingEndpointUrl?: string  // 仅local模型需要，Ollama endpoint URL
}

export interface NotificationsSettings {
  emailEnabled: boolean
  emailRecipients: string[]
  slackEnabled: boolean
  slackWebhook: string  // 总是空字符串
  hasSlackWebhook?: boolean  // 新增：指示是否有设置的Slack Webhook
  enableErrorAlerts: boolean
}

export interface IntegrationsSettings {
  enableWebhook: boolean
  webhookUrl: string  // 总是空字符串
  hasWebhookUrl?: boolean  // 新增：指示是否有设置的Webhook URL
  enableAPI: boolean
  apiRateLimit: number
  allowedOrigins: string[]
}


export interface SystemSettings {
  general: GeneralSettings
  ai: AISettings
  notifications: NotificationsSettings
  integrations: IntegrationsSettings
  style?: CompanyStyle
}

export interface SettingsState {
  settings: SystemSettings
  isLoading: boolean
  isSaving: boolean
  error: string | null
  hasUnsavedChanges: boolean

  // Actions
  fetchSettings: () => Promise<void>
  updateSettings: (updates: Partial<SystemSettings>) => void
  saveSettings: () => Promise<void>
  resetSettings: () => void
  validateSettings: () => boolean
}

const defaultSettings: SystemSettings = {
  general: {
    language: 'zh-CN',
    timezone: 'Asia/Shanghai',
  },
  ai: {
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 2000,
    enableContextWindow: true,
    contextWindow: 64000,
    maxResponseChars: 0,
    enableStreaming: false,
    apiKey: '',  // 总是空字符串
    hasApiKey: false,  // 默认没有设置API密钥
    embeddingModel: 'local',
    embeddingApiKey: '',  // 总是空字符串
    hasEmbeddingApiKey: false,  // 默认没有设置嵌入API密钥
    embeddingEndpointUrl: '',
  },
  notifications: {
    emailEnabled: false,
    emailRecipients: [],
    slackEnabled: false,
    slackWebhook: '',  // 总是空字符串
    hasSlackWebhook: false,  // 默认没有设置Slack Webhook
    enableErrorAlerts: false,
  },
  integrations: {
    enableWebhook: false,
    webhookUrl: '',  // 总是空字符串
    hasWebhookUrl: false,  // 默认没有设置Webhook URL
    enableAPI: true,
    apiRateLimit: 100,
    allowedOrigins: ['https://admin.example.com'],
  },
  style: undefined, // 风格配置从租户API单独获取
}

export { defaultSettings }

export const useSettingsStore = create<SettingsState>()(
  devtools(
    (set, get) => ({
      settings: defaultSettings,
      isLoading: false,
      isSaving: false,
      error: null,
      hasUnsavedChanges: false,

      fetchSettings: async () => {
        set({ isLoading: true, error: null })
        try {
          // 调用真实API获取设置
          const response = await settingsApi.getSettings()
          const backendSettings = response
          console.log('获取到的后端设置:', backendSettings)
          console.log('嵌入模型字段:', backendSettings.ai?.embedding_model)

          // 转换后端数据格式以匹配前端接口（snake_case -> camelCase）
          const settings: SystemSettings = {
            general: {
              language: backendSettings.general?.language || defaultSettings.general.language,
              timezone: backendSettings.general?.timezone || defaultSettings.general.timezone
            },
            ai: {
              model: backendSettings.ai?.model ?? defaultSettings.ai.model,
              temperature: backendSettings.ai?.temperature ?? defaultSettings.ai.temperature,
              maxTokens: backendSettings.ai?.max_tokens ?? defaultSettings.ai.maxTokens,
              enableContextWindow: backendSettings.ai?.enable_context_window ?? defaultSettings.ai.enableContextWindow,
              contextWindow: backendSettings.ai?.context_window ?? defaultSettings.ai.contextWindow,
              maxResponseChars: backendSettings.ai?.max_response_chars ?? defaultSettings.ai.maxResponseChars,
              enableStreaming: backendSettings.ai?.enable_streaming ?? defaultSettings.ai.enableStreaming,
              apiKey: '',  // 总是使用空字符串
              hasApiKey: backendSettings.ai?.has_api_key ?? defaultSettings.ai.hasApiKey,
              // 嵌入模型配置优先从后端设置读取，没有则使用默认值
              embeddingModel: backendSettings.ai?.embedding_model ?? defaultSettings.ai.embeddingModel,
              embeddingApiKey: '',  // 总是使用空字符串
              hasEmbeddingApiKey: backendSettings.ai?.has_embedding_api_key ?? defaultSettings.ai.hasEmbeddingApiKey,
              embeddingEndpointUrl: backendSettings.ai?.embedding_endpoint_url ?? defaultSettings.ai.embeddingEndpointUrl
            },
            notifications: {
              emailEnabled: backendSettings.notifications?.email_enabled || defaultSettings.notifications.emailEnabled,
              emailRecipients: backendSettings.notifications?.email_recipients || defaultSettings.notifications.emailRecipients,
              slackEnabled: backendSettings.notifications?.slack_enabled || defaultSettings.notifications.slackEnabled,
              slackWebhook: '',  // 总是使用空字符串
              hasSlackWebhook: backendSettings.notifications?.has_slack_webhook ?? defaultSettings.notifications.hasSlackWebhook,
              enableErrorAlerts: backendSettings.notifications?.enable_error_alerts || defaultSettings.notifications.enableErrorAlerts
            },
            integrations: {
              enableWebhook: backendSettings.integrations?.enable_webhook || defaultSettings.integrations.enableWebhook,
              webhookUrl: '',  // 总是使用空字符串
              hasWebhookUrl: backendSettings.integrations?.has_webhook_url ?? defaultSettings.integrations.hasWebhookUrl,
              enableAPI: backendSettings.integrations?.enable_api || defaultSettings.integrations.enableAPI,
              apiRateLimit: backendSettings.integrations?.api_rate_limit || defaultSettings.integrations.apiRateLimit,
              allowedOrigins: backendSettings.integrations?.allowed_origins || defaultSettings.integrations.allowedOrigins
            }
          }
          set({ settings, isLoading: false })
        } catch (error) {
          console.error('获取设置失败:', error)
          set({ error: '获取设置失败，请检查后端服务', isLoading: false })
        }
      },

      updateSettings: (updates) => {
        set((state) => ({
          settings: {
            ...state.settings,
            ...updates,
          },
          hasUnsavedChanges: true,
        }))
      },

      saveSettings: async () => {
        const { settings, validateSettings } = get()
        console.debug('开始验证设置...')
        if (!validateSettings()) {
          console.debug('设置验证失败，具体错误信息已在validateSettings中设置')
          return
        }

        console.log('保存前的设置:', settings)
        console.log('嵌入模型值:', settings.ai.embeddingModel)

        console.debug('验证通过，开始保存设置...')
        set({ isSaving: true, error: null })
        try {
          // 转换前端设置到后端格式（camelCase -> snake_case）
          // AI 字段：空值不发送，由后端保留原值
          const aiPayload: Record<string, any> = {
            model: settings.ai.model,
            temperature: settings.ai.temperature,
            max_tokens: settings.ai.maxTokens,
            enable_context_window: settings.ai.enableContextWindow,
            context_window: settings.ai.contextWindow,
            max_response_chars: settings.ai.maxResponseChars,
            enable_streaming: settings.ai.enableStreaming,
          }
          // 敏感字段：有值才发送，空字符串不发送
          if (settings.ai.apiKey?.trim()) {
            aiPayload.api_key = settings.ai.apiKey
          }
          if (settings.ai.embeddingModel) {
            aiPayload.embedding_model = settings.ai.embeddingModel
          }
          if (settings.ai.embeddingApiKey?.trim()) {
            aiPayload.embedding_api_key = settings.ai.embeddingApiKey
          }
          if (settings.ai.embeddingEndpointUrl?.trim()) {
            aiPayload.embedding_endpoint_url = settings.ai.embeddingEndpointUrl
          }

          const backendSettings = {
            general: {
              language: settings.general.language,
              timezone: settings.general.timezone
            },
            ai: aiPayload,
            notifications: {
              email_enabled: settings.notifications.emailEnabled,
              email_recipients: settings.notifications.emailRecipients,
              slack_enabled: settings.notifications.slackEnabled,
              slack_webhook: settings.notifications.slackWebhook,
              enable_error_alerts: settings.notifications.enableErrorAlerts
            },
            integrations: {
              enable_webhook: settings.integrations.enableWebhook,
              webhook_url: settings.integrations.webhookUrl,
              enable_api: settings.integrations.enableAPI,
              api_rate_limit: settings.integrations.apiRateLimit,
              allowed_origins: settings.integrations.allowedOrigins
             }
           }

           // 创建安全版本的backendSettings用于日志（掩码敏感字段）
           const safeAi = { ...backendSettings.ai }
           if ('api_key' in safeAi) safeAi.api_key = '[MASKED]'
           if ('embedding_api_key' in safeAi) safeAi.embedding_api_key = '[MASKED]'
           const safeBackendSettings = {
             ...backendSettings,
             ai: safeAi
           }
           
           console.debug('发送到后端的设置:', safeBackendSettings)
           console.debug('AI模型配置详情:', {
             model: backendSettings.ai.model,
             has_api_key: 'api_key' in backendSettings.ai,
             embedding_model: backendSettings.ai.embedding_model,
             has_embedding_api_key: 'embedding_api_key' in backendSettings.ai
           })

           // 调用真实API保存设置
           await settingsApi.updateSettings(backendSettings as Partial<ApiSystemSettings>)
          set({ isSaving: false, hasUnsavedChanges: false })
        } catch (error) {
          console.error('保存设置失败:', error)
          set({ error: '保存设置失败，请检查后端服务', isSaving: false })
        }
      },

      resetSettings: () => {
        set({
          settings: defaultSettings,
          hasUnsavedChanges: false,
          error: null,
        })
      },

      validateSettings: () => {
        const { settings } = get()
        
        console.debug('开始验证设置:', {
          model: settings.ai.model,
          hasApiKey: settings.ai.hasApiKey,
          apiKeyLength: settings.ai.apiKey?.length || 0
        })

        // 定义需要API密钥的模型
        const API_KEY_REQUIRED_MODELS = new Set([
          'gpt-4',
          'gpt-3.5-turbo', 
          'claude-3',
          'deepseek-chat',
          'deepseek-reasoner'
        ])

        // 验证AI设置
        // 1. 模型选择必填验证
        if (!settings.ai.model?.trim()) {
          console.debug('验证失败: 未选择AI模型')
          set({ error: '请选择AI模型' })
          return false
        }

        // 2. API密钥条件验证
        const selectedModel = settings.ai.model
        const hasApiKey = settings.ai.hasApiKey
        const apiKeyValue = settings.ai.apiKey?.trim() || ''
        
        console.debug('API密钥验证:', {
          selectedModel,
          hasApiKey,
          apiKeyValueLength: apiKeyValue.length,
          requiresApiKey: API_KEY_REQUIRED_MODELS.has(selectedModel)
        })
        
        if (API_KEY_REQUIRED_MODELS.has(selectedModel)) {
          // 需要API密钥的模型
          console.debug(`模型${selectedModel}需要API密钥，检查中...`)
          if (!hasApiKey && !apiKeyValue) {
            console.debug(`验证失败: 模型${selectedModel}需要API密钥，但未提供`)
            set({ error: `选择${selectedModel}模型需要提供API密钥` })
            return false
          }
          console.debug(`API密钥验证通过: hasApiKey=${hasApiKey}, apiKeyValue长度=${apiKeyValue.length}`)
        } else if (selectedModel === 'local-model') {
          // 本地模型不需要API密钥，可以忽略
          console.debug('本地模型，跳过API密钥验证')
        } else {
          // 其他模型（未知模型）提示
          console.warn(`未知模型: ${selectedModel}，跳过API密钥验证`)
        }

        // 3. 温度值验证
        if (settings.ai.temperature < 0 || settings.ai.temperature > 1) {
          set({ error: '温度值必须在0-1之间' })
          return false
        }

        // 4. Token数量验证
        if (settings.ai.maxTokens < 100 || settings.ai.maxTokens > 4000) {
          set({ error: '最大Token数必须在100-4000之间' })
          return false
        }

        // 5. 嵌入模型验证（条件验证）
        const embeddingModel = settings.ai.embeddingModel
        const hasEmbeddingApiKey = settings.ai.hasEmbeddingApiKey
        const embeddingApiKeyValue = settings.ai.embeddingApiKey?.trim() || ''
        const embeddingEndpointUrl = settings.ai.embeddingEndpointUrl?.trim() || ''

        // 检查用户是否修改了嵌入模型配置
        const isEmbeddingConfigModified = (
          embeddingModel !== defaultSettings.ai.embeddingModel ||
          embeddingApiKeyValue !== '' ||
          embeddingEndpointUrl !== ''
        )

        console.debug('嵌入模型验证:', {
          embeddingModel,
          hasEmbeddingApiKey,
          embeddingApiKeyValue,
          embeddingEndpointUrl,
          isEmbeddingConfigModified,
          defaultEmbeddingModel: defaultSettings.ai.embeddingModel
        })

        // 只有用户修改了嵌入模型配置时才验证
        if (isEmbeddingConfigModified) {
          console.debug('嵌入模型配置已修改，开始验证...')
          if (embeddingModel === 'dashscope') {
            if (!hasEmbeddingApiKey && !embeddingApiKeyValue) {
              set({ error: '选择DashScope嵌入模型需要提供API密钥' })
              return false
            }
          } else if (embeddingModel === 'local') {
            if (!embeddingEndpointUrl) {
              set({ error: '选择本地嵌入模型需要提供端点URL' })
              return false
            }
            try {
              new URL(embeddingEndpointUrl)
            } catch {
              set({ error: '嵌入模型端点URL格式不正确' })
              return false
            }
          } else if (embeddingModel) {
            console.warn(`未知嵌入模型: ${embeddingModel}`)
          }
        } else {
          console.debug('嵌入模型配置未修改，跳过验证')
        }

        // 验证通知设置
        if (settings.notifications.emailEnabled && settings.notifications.emailRecipients.length === 0) {
          set({ error: '启用邮件通知时需要至少一个收件人' })
          return false
        }

        if (settings.notifications.slackEnabled && !settings.notifications.slackWebhook.trim()) {
          set({ error: '启用Slack通知时需要Webhook URL' })
          return false
        }

        // 验证集成设置
        if (settings.integrations.enableWebhook) {
          try {
            new URL(settings.integrations.webhookUrl)
          } catch {
            set({ error: 'Webhook URL格式不正确' })
            return false
          }
        }

        if (settings.integrations.apiRateLimit < 1 || settings.integrations.apiRateLimit > 1000) {
          set({ error: 'API速率限制必须在1-1000之间' })
          return false
        }

        console.debug('所有设置验证通过')
        return true
      },
    }),
    { name: 'SettingsStore' }
  )
)